import { useCallback, useEffect, useRef, useState } from 'react';
import { pricingApi, type QuoteLineRequest } from '../lib/distributorsApi';
import { ABC_MAX_QUOTE_LINES } from '../components/distributors/abcConstants';
import { useDebounce } from './useDebounce';
import type { ApiCartLine, LinePriceState } from '../components/distributors/ApiOrderCart';

interface UseAbcQuoteArgs {
  distributorId: number | null;
  enabled: boolean;
  shipToNumber: string | null;
  branchNumber: string | null;
  lines: ApiCartLine[];
  maxQuoteLines?: number;
}

interface UseAbcQuoteResult {
  pricing: Record<string, LinePriceState>;
  quoting: boolean;
  /**
   * True between a cart/context change and the debounced re-quote starting.
   * In that window `quoting` is still false while `pricing` no longer matches
   * the cart, so submitting would send the branch stale or unpriced lines.
   */
  quoteStale: boolean;
  quoteError: string | null;
  connectionLost: boolean;
  refresh: () => void;
}

/**
 * Debounced, chunked live pricing for the API cart.
 *
 * Deliberately NOT react-query: this is a POST against a rapidly-changing
 * payload with per-line partial results — imperative state with a
 * stale-response guard is simpler than cache-key churn.
 *
 * Dimensional lines without a chosen length stay `idle` (excluded from the
 * payload — ABC would fail them anyway). Per ABC: a $0.00 price with an OK
 * status is a VALID price, and single lines can fail while others succeed.
 */
export function useAbcQuote({
  distributorId,
  enabled,
  shipToNumber,
  branchNumber,
  lines,
  maxQuoteLines = ABC_MAX_QUOTE_LINES,
}: UseAbcQuoteArgs): UseAbcQuoteResult {
  const [pricing, setPricing] = useState<Record<string, LinePriceState>>({});
  const [quoting, setQuoting] = useState(false);
  const [pricingSignature, setPricingSignature] = useState('');
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [connectionLost, setConnectionLost] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const refresh = () => setRefreshCounter(value => value + 1);
    const timer = window.setInterval(refresh, 5 * 60 * 1000);
    const visible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', visible);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', visible); };
  }, [enabled]);
  const requestIdRef = useRef(0);
  const linesRef = useRef(lines);
  linesRef.current = lines;

  // A stable signature of everything that affects prices.
  const signature = JSON.stringify({
    distributorId,
    shipToNumber,
    branchNumber,
    refreshCounter,
    maxQuoteLines,
    lines: lines.map((line) => [
      line.lineId, line.item_number, line.uom, line.quantity,
      line.is_dimensional, line.length_value ?? null, line.length_uom ?? null,
    ]),
  });
  const debouncedSignature = useDebounce(signature, 600);
  const latestSignature = useRef(signature);
  latestSignature.current = signature;

  useEffect(() => {
    if (!enabled || !distributorId || !shipToNumber || !branchNumber) {
      setPricing({});
      setQuoting(false);
      return;
    }

    if (signature !== debouncedSignature) return;
    const quotedSignature = signature;
    const currentLines = linesRef.current;
    if (currentLines.length === 0) {
      setPricing({});
      setQuoting(false);
      return;
    }

    // Lines missing a required length are idle; everything else gets priced.
    const quotable = currentLines.filter(
      (line) => !line.is_dimensional || line.length_value != null,
    );
    const initial: Record<string, LinePriceState> = {};
    currentLines.forEach((line) => {
      initial[line.lineId] = quotable.includes(line) ? { state: 'pending' } : { state: 'idle' };
    });
    setPricing(initial);
    setPricingSignature(quotedSignature);

    if (quotable.length === 0) {
      setQuoting(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setQuoting(true);
    setQuoteError(null);

    const chunks: QuoteLineRequest[][] = [];
    for (let start = 0; start < quotable.length; start += maxQuoteLines) {
      chunks.push(
        quotable.slice(start, start + maxQuoteLines).map((line) => ({
          id: line.lineId,
          item_number: line.item_number,
          quantity: line.quantity,
          uom: line.uom || undefined,
          is_dimensional: line.is_dimensional,
          length_value: line.length_value ?? undefined,
          length_uom: line.length_uom || undefined,
        })),
      );
    }

    Promise.all(
      chunks.map((chunk) =>
        pricingApi.quote(distributorId, {
          ship_to_number: shipToNumber,
          branch_number: branchNumber,
          purpose: 'ordering',
          lines: chunk,
        }),
      ),
    )
      .then((responses) => {
        if (requestId !== requestIdRef.current || latestSignature.current !== quotedSignature) return; // stale — a newer quote superseded this one
        setPricing((previous) => {
          const next = { ...previous };
          responses.forEach((response) => {
            response.lines.forEach((line) => {
              next[line.id] = line.ok
                ? {
                    state: 'priced',
                    unit_price: parseFloat(line.unit_price ?? '0') || 0,
                    currency: line.currency || 'USD',
                  }
                : {
                    state: 'error',
                    status_code: line.status_code,
                    message: line.status_message || 'Not priced',
                  };
            });
          });
          return next;
        });
        setConnectionLost(false);
      })
      .catch((error: any) => {
        if (requestId !== requestIdRef.current || latestSignature.current !== quotedSignature) return;
        const data = error?.response?.data ?? error?.responseData;
        const code = data?.code;
        if (code === 'connection_required' || code === 'connection_error') {
          setConnectionLost(true);
        }
        setQuoteError(data?.error || error?.message || 'Live pricing failed');
        // Leave lines in pending→idle so the UI doesn't show stale prices.
        setPricing((previous) => {
          const next: Record<string, LinePriceState> = {};
          Object.keys(previous).forEach((key) => {
            next[key] = previous[key].state === 'pending' ? { state: 'idle' } : previous[key];
          });
          return next;
        });
      })
      .finally(() => {
        if (requestId === requestIdRef.current && latestSignature.current === quotedSignature) setQuoting(false);
      });
    return () => { ++requestIdRef.current; };
  }, [debouncedSignature, enabled, signature]);

  const refresh = useCallback(() => setRefreshCounter((count) => count + 1), []);

  const quoteStale = signature !== debouncedSignature;

  // Never display the previous UOM/account's price during the debounce gap.
  const currentPricing: Record<string, LinePriceState> = pricingSignature === signature
    ? pricing
    : Object.fromEntries(lines.map(line => [line.lineId, {
        state: enabled && (!line.is_dimensional || line.length_value != null) ? 'pending' : 'idle',
      } as LinePriceState]));
  return { pricing: currentPricing, quoting, quoteStale, quoteError, connectionLost, refresh };
}
